<template>
  <layout-report-step
    :isDone="isDone"
    :title="header.title"
    :description="header.description"
    :image="header.image"
    :debug="header.debug"
  >
    <v-card class="pt-4 pr-4 pl-4" flat max-width="800">
      <v-form v-model="valid">
        <v-autocomplete
          outlined
          :label="$t('report.accidentDetails.role.label')"
          v-model="accidentDetails.role"
          persistent-hint
          :hint="$t('report.accidentDetails.role.hint')"
          :items="roles"
          :rules="requiredRule"
          item-text="text"
          item-value="value"
        ></v-autocomplete>
        <v-menu
          v-model="menu2"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="localeDateFormat"
              :label="$t('report.accidentDetails.date')"
              outlined
              readonly
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="formatedDate"
            @input="menu2 = false"
            :max="new Date().toISOString().substr(0, 10)"
            color="primary"
            first-day-of-week="1"
            :locale="$t('report.datePicker.locale')"
          ></v-date-picker>
        </v-menu>
        <v-menu
          ref="menu"
          v-model="timepickerMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="formatedtime"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="formatedtime"
              :label="$t('report.accidentDetails.time')"
              readonly
              outlined
              v-on="on"
            ></v-text-field>
          </template>
          <v-time-picker
            v-if="timepickerMenu"
            v-model="formatedtime"
            ampm-in-title
            format="24hr"
            full-width
            @click:minute="$refs.menu.save(formatedtime)"
          ></v-time-picker>
        </v-menu>
        <v-radio-group
          v-model="isAtAccidentPlace"
          :label="$t('report.accidentDetails.location.title')"
          @change="getPositionIfTrue"
        >
          <v-radio :label="$t('report.accidentDetails.location.true')" :value="1"></v-radio>
          <v-radio :label="$t('report.accidentDetails.location.false')" :value="2"></v-radio>
        </v-radio-group>
        <span v-if="isAtAccidentPlace">
          <v-radio-group v-model="isAddress" :label="$t('report.accidentDetails.location.isAddress')">
            <v-radio :label="$t('report.accidentDetails.location.true')" :value="true"></v-radio>
            <v-radio :label="$t('report.accidentDetails.location.false')" :value="false"></v-radio>
          </v-radio-group>
          <span v-if="isAddress">
            <v-text-field
              :rules="requiredRule"
              v-model="accidentDetails.address.street"
              outlined
              :label="$t('report.accidentDetails.location.street')"
            >
            </v-text-field>
            <v-text-field
              v-model="accidentDetails.address.zip"
              outlined
              :label="$t('report.accidentDetails.location.zip')"
            >
            </v-text-field>
            <v-text-field
              :rules="requiredRule"
              v-model="accidentDetails.address.city"
              outlined
              :label="$t('report.accidentDetails.location.city')"
            >
            </v-text-field>
          </span>
          <span v-else>
            <v-textarea
              :rules="requiredRule"
              v-model="accidentDetails.comment"
              outlined
              persistent-hint
              :label="$t('report.accidentDetails.location.comment.label')"
              :hint="$t('report.accidentDetails.location.comment.hint')"
            >
            </v-textarea>
          </span>
        </span>
      </v-form>
    </v-card>
  </layout-report-step>
</template>

<script lang="ts">
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { ReportRoleEnum } from "@/lib/enum/ReportRole.enum";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { IReportHeaderData } from "@/lib/interfaces/Report/IReportHeaderData";
import { IReportStep } from "@/lib/interfaces/Report/IViewReportStep";
import { requiredRule } from "@/lib/rules/requiredRule";
import { DateVuetifyHelper } from "@/lib/utility/date-helper";
import ReportStepMixin from "@/mixins/ReportStepMixin.vue";
import { MrfiktivCreateAccidentDetailsDtoGen } from "@/services/mrfiktiv/v1/data-contracts";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ReportModule } from "@/store/modules/report.store";
import { Component } from "vue-property-decorator";
import { AddressWithGeo } from "@/models/address.entity";

@Component({
  components: {
    LayoutReportStep
  }
})
export default class ReportAccidentDetails extends ReportStepMixin implements IReportStep {
  header: IReportHeaderData = {
    title: "report.accidentDetails.title",
    description: "report.accidentDetails.description"
  };

  screen = ReportScreenEnum.accidentdetails;

  isLoadingGeo = false;

  menu2 = false;

  get isAtAccidentPlace(): boolean | number {
    return ReportModule.isAtAccidentPlace;
  }

  set isAtAccidentPlace(isAtAccidentPlace: boolean | number) {
    ReportModule.setIsAtAccidentPlace(isAtAccidentPlace);
  }

  get isAddress(): boolean {
    return ReportModule.isAddress;
  }

  set isAddress(isAddress: boolean) {
    ReportModule.setIsAddress(isAddress);
  }

  get roles() {
    return [
      {
        text: this.$t("report.accidentDetails.role.responsible"),
        value: ReportRoleEnum.RESPONSIBLE
      },
      {
        text: this.$t("report.accidentDetails.role.victim"),
        value: ReportRoleEnum.VICTIM
      },
      {
        text: this.$t("report.accidentDetails.role.unclear"),
        value: ReportRoleEnum.UNCLEAR
      }
    ];
  }

  get requiredRule() {
    return [requiredRule()];
  }

  get localeDateFormat() {
    return new DateVuetifyHelper(this.date).localeDate;
  }

  /**
   * Gets date as iso string
   */
  get date(): string {
    if (!this.accidentDetails.date) {
      this.date = new Date().toISOString();
    }
    return this.accidentDetails.date;
  }

  /**
   * Set date (iso date string requried)
   */
  set date(date: string) {
    ReportModule.accidentDetails.date = date;
  }

  get formatedDate() {
    return new DateVuetifyHelper(this.date).date;
  }

  set formatedDate(date) {
    const newDate = new DateVuetifyHelper(this.date);
    newDate.date = date;
    this.date = newDate.isoDate;
  }

  get formatedtime() {
    return new DateVuetifyHelper(this.date).time;
  }

  set formatedtime(time) {
    const date = new DateVuetifyHelper(this.date);
    date.time = time;
    this.date = date.isoDate;
  }

  get accidentDetails() {
    return ReportModule.accidentDetails;
  }

  set accidentDetails(accidentDetails: MrfiktivCreateAccidentDetailsDtoGen) {
    ReportModule.mutateAccidentDetails(accidentDetails);
  }

  valid = false;
  timepickerMenu = false;

  geolocationCoordinates = {
    latitude: 0,
    longitude: 0,
    accuracy: 0
  };

  get geo() {
    return this.geolocationCoordinates;
  }

  async getPositionIfTrue() {
    if (this.isAtAccidentPlace == 1) {
      await this.getLocation();
    } else {
      if (!this.accidentDetails.address) {
        this.accidentDetails.address = new AddressWithGeo();
      }

      this.accidentDetails.address.geo.lat = 0;
      this.accidentDetails.address.geo.lng = 0;
    }
  }

  async getLocation() {
    this.isLoadingGeo = true;
    const geo: any = await new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        reject(new Error("Geolocation is not available."));
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve(pos);
        },
        (err) => {
          reject(err);
        }
      );
    });

    if (!this.accidentDetails.address) {
      this.accidentDetails.address = new AddressWithGeo();
    }

    this.accidentDetails.address.geo.lat = geo.coords.latitude;
    this.accidentDetails.address.geo.lng = geo.coords.longitude;

    this.isLoadingGeo = false;
  }

  get isDone() {
    return this.valid && this.isAtAccidentPlace != 0;
  }

  get color() {
    return getDefaultPartnerColor();
  }
}
</script>

<style scoped>
.center {
  text-align: center;
}
</style>
